import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CircleAlert,
  ImageOff,
  Info,
  Redo2,
  SearchX,
  TriangleAlert,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import ConfirmDialog from "@/components/ConfirmDialog";
import Spinner from "@/components/Spinner";
import StatusScreen, { FullPageSpinner } from "@/components/StatusScreen";
import { cn } from "@/lib/utils";
import useOrganization from "../../hooks/useOrganization";
import {
  applyMasterImageRenders,
  getBrickSeries,
  saveMasterImageDraft,
  uploadBrickRenders,
} from "../../utils";
import { applyMasterImage } from "../../masterImages/apply";
import { loadImage, renderRegionPng } from "../../masterImages/canvas";
import { unappliedChanges } from "../../masterImages/changes";
import { detectBricks } from "../../masterImages/detection";
import {
  draftPayload,
  canRedo,
  canUndo,
  draftReducer,
  type DraftAction,
  editedRegionCount,
  initDraft,
  regionFromDetected,
} from "../../masterImages/draft";
import { rotatedSize, scaleRect } from "../../masterImages/geometry";
import type { Brick, BrickSeries, MasterImage } from "../../masterImages/types";
import EditorCanvas, { type StrokePreview, type Tool } from "./EditorCanvas";
import SetupPanel, { type SetupValues } from "./SetupPanel";
import Toolbar from "./Toolbar";
import { useAutosave } from "./useAutosave";
import { ownsKeyboard } from "./keyboard";
import { useOutlines } from "./useOutlines";
import { useWorkingImage } from "./useWorkingImage";

const SAVE_LABELS = {
  saved: "Opgeslagen",
  pending: "Niet-opgeslagen wijzigingen",
  saving: "Opslaan…",
  error: "Opslaan mislukt",
};

interface EditorProps {
  series: BrickSeries;
  initial: MasterImage;
}

const Editor: React.FC<EditorProps> = ({ series, initial }) => {
  const organization = useOrganization();
  const navigate = useNavigate();
  const [draft, rawDispatch] = useReducer(draftReducer, initial, initDraft);
  // Apply renders the draft as it was when it started; an edit saved meanwhile would make the
  // apply request refer to regions that no longer match.
  const applyingRef = useRef(false);
  const dispatch = useCallback((action: DraftAction) => {
    if (applyingRef.current && action.type !== "select") {
      toast.info("Wacht tot het toepassen klaar is.");
      return;
    }
    rawDispatch(action);
  }, []);
  const { masterImage } = draft;
  const [bricks, setBricks] = useState<Brick[]>(series.bricks);
  const [setup, setSetup] = useState<SetupValues>({
    rotation: initial.rotation,
    brickWidthMm: initial.brickWidthMm,
    brickHeightMm: initial.brickHeightMm,
  });
  useEffect(() => {
    setSetup({
      rotation: masterImage.rotation,
      brickWidthMm: masterImage.brickWidthMm,
      brickHeightMm: masterImage.brickHeightMm,
    });
  }, [masterImage.rotation, masterImage.brickWidthMm, masterImage.brickHeightMm]);
  const [tool, setTool] = useState<Tool>("select");
  const [brushRadius, setBrushRadius] = useState(12);
  const [guides, setGuides] = useState(false);
  const [applying, setApplying] = useState<{ done: number; total: number } | null>(null);
  const [confirmingRedetect, setConfirmingRedetect] = useState(false);
  const original = useRef<Promise<HTMLImageElement> | null>(null);

  const { working, error } = useWorkingImage(masterImage, setup.rotation);
  const rotationPending = setup.rotation !== masterImage.rotation;
  const [strokePreview, setStrokePreview] = useState<StrokePreview | null>(null);
  const outlineSource = useMemo(
    () =>
      strokePreview
        ? {
            ...masterImage,
            regions: masterImage.regions.map((r) =>
              r.id === strokePreview.id
                ? { ...r, strokes: [...r.strokes, strokePreview.stroke] }
                : r,
            ),
          }
        : masterImage,
    [masterImage, strokePreview],
  );
  const outlines = useOutlines(
    rotationPending ? null : (working?.pixels ?? null),
    working?.scale ?? 1,
    outlineSource,
  );

  const save = useCallback(
    (mi: MasterImage) => saveMasterImageDraft(organization, series._id, mi._id, draftPayload(mi)),
    [organization, series._id],
  );
  const autosave = useAutosave(masterImage, save);

  const detected = masterImage.brickWidthMm != null;
  const setupChanged =
    rotationPending ||
    setup.brickWidthMm !== masterImage.brickWidthMm ||
    setup.brickHeightMm !== masterImage.brickHeightMm;

  const runDetection = () => {
    if (!working || setup.brickWidthMm == null || setup.brickHeightMm == null) return;
    const result = detectBricks({
      image: working.pixels,
      brickWidthMm: setup.brickWidthMm,
      brickHeightMm: setup.brickHeightMm,
    });
    dispatch({
      type: "redetect",
      rotation: setup.rotation,
      brickWidthMm: setup.brickWidthMm,
      brickHeightMm: setup.brickHeightMm,
      pxPerMm: result.pxPerMm == null ? null : result.pxPerMm / working.scale,
      regions: result.regions.map((r) =>
        regionFromDetected({ ...r, ...scaleRect(r, 1 / working.scale) }, crypto.randomUUID()),
      ),
    });
    if (result.regions.length === 0)
      toast.warning("Geen stenen gevonden. Controleer de steenmaat of teken regio's met de hand.");
  };

  const commitSetup = () => {
    if (!detected && setup.brickWidthMm != null && setup.brickHeightMm != null) redetect();
  };

  const redetect = () => {
    if (masterImage.regions.length === 0) return runDetection();
    setConfirmingRedetect(true);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (ownsKeyboard(e.target)) return;
      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && (key === "z" || key === "y")) {
        e.preventDefault();
        dispatch({ type: key === "y" || e.shiftKey ? "redo" : "undo" });
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const step = e.shiftKey ? 10 : 1;
      const moves: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      if (moves[e.key]) {
        e.preventDefault();
        dispatch({ type: "move", dx: moves[e.key][0], dy: moves[e.key][1] });
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        dispatch({ type: "delete" });
      } else if (e.key === "f") {
        dispatch({ type: "flipType" });
      } else if (e.key === "Escape") {
        dispatch({ type: "select", ids: [] });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch]);

  const unapplied = unappliedChanges(masterImage, bricks);
  const selected = useMemo(
    () => masterImage.regions.filter((r) => draft.selection.includes(r.id)),
    [masterImage.regions, draft.selection],
  );

  const apply = async () => {
    applyingRef.current = true;
    setApplying({ done: 0, total: 0 });
    try {
      await autosave.flush();
      original.current ??= loadImage(masterImage.image);
      const image = await original.current;
      const updated = await applyMasterImage(masterImage, bricks, {
        render: (region) => renderRegionPng(image, masterImage, region),
        upload: (blobs) => uploadBrickRenders(organization, series._id, blobs),
        apply: (renders) =>
          applyMasterImageRenders(organization, series._id, masterImage._id, renders),
        onProgress: (done, total) => setApplying({ done, total }),
      });
      setBricks(updated.bricks);
      toast.success("Toegepast op de collectie");
    } catch (e) {
      original.current = null;
      toast.error((e as Error).message);
    } finally {
      applyingRef.current = false;
      setApplying(null);
    }
  };

  const back = async () => {
    await autosave.flush().catch(() => undefined);
    navigate(`/${organization}/series/${series._id}`);
  };

  const resolution = masterImage.pxPerMm;
  const bounds = rotatedSize(masterImage.width, masterImage.height, setup.rotation);

  const saveFailed = autosave.status === "error";

  return (
    <div className="flex h-full flex-col bg-background">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b px-3">
        <Button variant="ghost" size="icon-sm" onClick={back} aria-label="Terug naar de collectie">
          <ArrowLeft />
        </Button>
        <div className="grid min-w-0 flex-1 leading-tight">
          <span className="truncate text-sm font-semibold">{series.name}</span>
          <span className="text-xs text-muted-foreground">Master image</span>
        </div>
        <span
          className={cn(
            "hidden items-center gap-1.5 text-sm sm:flex",
            saveFailed ? "text-destructive" : "text-muted-foreground",
          )}
          role="status"
        >
          {autosave.status === "saving" && <Spinner className="size-3.5" />}
          {saveFailed && autosave.error
            ? `Opslaan mislukt: ${autosave.error}`
            : SAVE_LABELS[autosave.status]}
        </span>
        <Separator orientation="vertical" className="data-[orientation=vertical]:h-5" />
        <div className="flex">
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={!canUndo(draft)}
            onClick={() => dispatch({ type: "undo" })}
            aria-label="Ongedaan maken"
            title="Ongedaan maken (⌘/Ctrl+Z)"
          >
            <Undo2 />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={!canRedo(draft)}
            onClick={() => dispatch({ type: "redo" })}
            aria-label="Opnieuw"
            title="Opnieuw (⌘/Ctrl+Shift+Z)"
          >
            <Redo2 />
          </Button>
        </div>
        <Badge variant="secondary" className="tabular-nums">
          {masterImage.regions.length} {masterImage.regions.length === 1 ? "regio" : "regio's"}
        </Badge>
        {unapplied > 0 ? (
          <Badge variant="warning" className="tabular-nums">
            {unapplied} niet toegepast
          </Badge>
        ) : masterImage.regions.length > 0 ? (
          <Badge variant="outline">Toegepast</Badge>
        ) : null}
        <Button
          onClick={apply}
          disabled={applying != null || unapplied === 0 || resolution == null || rotationPending}
        >
          {applying != null && <Spinner />}
          Toepassen op collectie
        </Button>
      </header>
      <div className="flex min-h-0 flex-1">
        <aside className="w-[360px] shrink-0 overflow-y-auto border-r p-4">
          <div className="grid gap-5">
            <SetupPanel
              values={setup}
              detected={detected}
              changed={setupChanged}
              onChange={setSetup}
              onCommit={commitSetup}
              onRedetect={redetect}
              guides={guides || rotationPending}
              onGuides={setGuides}
            />
            {resolution != null && resolution < 1 && (
              <Alert variant="destructive">
                <CircleAlert />
                <AlertDescription>
                  Resolutie te laag: {resolution.toFixed(2)} px/mm. Stenen hebben minstens 1 px/mm
                  nodig; gebruik een scherpere foto of een foto van dichterbij.
                </AlertDescription>
              </Alert>
            )}
            {resolution != null && resolution >= 1 && resolution < 1.5 && (
              <Alert variant="warning">
                <TriangleAlert />
                <AlertDescription>
                  Lage resolutie: {resolution.toFixed(2)} px/mm. Onder 1,5 px/mm kunnen stenen wazig
                  worden.
                </AlertDescription>
              </Alert>
            )}
            {resolution == null && detected && (
              <Alert>
                <Info />
                <AlertDescription>
                  Nog geen schaal: de detectie vond geen stenen. Teken een hele steen met een randje
                  voeg eromheen, zoals gedetecteerde regio's, zodat de schaal bekend is. Toepassen
                  heeft die nodig.
                </AlertDescription>
              </Alert>
            )}
            {rotationPending && detected && (
              <Alert>
                <Info />
                <AlertDescription>
                  Detecteer de stenen opnieuw om de nieuwe rotatie toe te passen.
                </AlertDescription>
              </Alert>
            )}
            <Toolbar
              tool={tool}
              onTool={setTool}
              brushRadius={brushRadius}
              onBrushRadius={setBrushRadius}
              tolerance={masterImage.tolerance}
              selected={selected}
              dispatch={dispatch}
            />
            {applying && applying.total > 0 && (
              <div className="grid gap-1.5 text-sm">
                <span className="text-muted-foreground tabular-nums">
                  Toepassen: {applying.done} van {applying.total}
                </span>
                <Progress value={(applying.done / applying.total) * 100} aria-label="Toepassen" />
              </div>
            )}
          </div>
        </aside>
        <main className="relative min-w-0 flex-1 bg-neutral-900">
          {error && (
            <div className="absolute inset-0 bg-background">
              <StatusScreen
                icon={ImageOff}
                tone="destructive"
                title="De master image kan niet worden geladen"
                description={error}
              />
            </div>
          )}
          {!error && !working && (
            <div className="absolute inset-0 flex items-center justify-center text-neutral-400">
              <Spinner className="size-6" />
            </div>
          )}
          {working && (
            <EditorCanvas
              image={working.canvas}
              imageScale={working.scale}
              bounds={bounds}
              regions={masterImage.regions}
              selection={draft.selection}
              outlines={outlines}
              showRegions={!rotationPending}
              tool={tool}
              brushRadius={brushRadius}
              guides={guides || rotationPending}
              dispatch={dispatch}
              onStrokePreview={setStrokePreview}
            />
          )}
        </main>
      </div>
      <ConfirmDialog
        open={confirmingRedetect}
        onOpenChange={setConfirmingRedetect}
        title="Stenen opnieuw detecteren?"
        description={`Dit verwijdert alle ${masterImage.regions.length} steenregio's, ook de ${editedRegionCount(masterImage.regions)} die je hebt bewerkt.`}
        confirmLabel="Opnieuw detecteren"
        onConfirm={runDetection}
      />
    </div>
  );
};

const MasterImageEditor: React.FC = () => {
  const organization = useOrganization();
  const { seriesId, masterImageId } = useParams();
  const [series, setSeries] = useState<BrickSeries | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    getBrickSeries(organization, seriesId).then(setSeries, (e: Error) =>
      setFailed(e.message),
    );
  }, [organization, seriesId]);

  const masterImage = series?.masterImages?.find((m) => m._id === masterImageId);
  const backToSeries = (
    <Button variant="outline" asChild>
      <Link to={`/${organization}/series/${seriesId}`}>Naar de collectie</Link>
    </Button>
  );
  if (failed)
    return (
      <StatusScreen
        icon={CircleAlert}
        tone="destructive"
        title="De collectie kan niet worden geladen"
        description={failed}
        actions={backToSeries}
      />
    );
  if (!series) return <FullPageSpinner />;
  if (!masterImage)
    return (
      <StatusScreen
        icon={SearchX}
        title="Master image niet gevonden"
        description="Misschien is hij verwijderd."
        actions={backToSeries}
      />
    );
  return <Editor series={series} initial={masterImage} />;
};

export default MasterImageEditor;
